import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Download, 
  RefreshCw, 
  Loader2, 
  Star, 
  StarOff, 
  Trash2,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { designsAPI, projectsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/config/firebase';
import { doc, updateDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { Progress } from '@/components/ui/progress';

const ProjectDialog = ({ open, onClose, project, onUpdate, onDelete }) => {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('ASPECT_1_1');
  const [generating, setGenerating] = useState(false);
  const [mainVersionId, setMainVersionId] = useState(project?.mainVersionId || project?.currentVersion);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateProgress, setRegenerateProgress] = useState(null);

  if (!project) return null;

  const versions = project.versions || [];
  const editCount = versions.length;
  const maxEdits = 10;
  const canEdit = editCount < maxEdits;
  const mainVersion = versions.find(v => v.versionId === mainVersionId) || versions[versions.length - 1];

  // Build prompt history by tracing back through ALL parent versions until version 1
  const buildPromptHistory = () => {
    const history = [];
    let currentVersion = mainVersion;
    const visited = new Set(); // Prevent infinite loops
    
    // Trace back through parent versions until we reach the original (no parent)
    while (currentVersion && !visited.has(currentVersion.versionId)) {
      visited.add(currentVersion.versionId);
      
      // Add current version to history
      history.push({
        versionId: currentVersion.versionId,
        prompt: currentVersion.originalPrompt,
        enhancedPrompt: currentVersion.enhancedPrompt,
        timestamp: currentVersion.createdAt
      });
      
      // Move to parent version if it exists
      if (currentVersion.parentVersionId) {
        const parentVersion = versions.find(v => v.versionId === currentVersion.parentVersionId);
        if (parentVersion) {
          currentVersion = parentVersion;
        } else {
          // Parent version not found, stop here
          break;
        }
      } else {
        // No parent version, we've reached the original
        break;
      }
    }
    
    // Return as-is to show newest first, oldest last
    return history;
  };

  const promptHistory = buildPromptHistory();

  const aspectRatios = [
    { value: 'ASPECT_1_1', label: 'Square (1:1)' },
    { value: 'ASPECT_16_9', label: 'Wide (16:9)' },
    { value: 'ASPECT_9_16', label: 'Tall (9:16)' },
    { value: 'ASPECT_4_3', label: 'Classic (4:3)' },
    { value: 'ASPECT_3_4', label: 'Portrait (3:4)' },
    { value: 'ASPECT_4_5', label: 'Portrait (4:5)' },
    { value: 'ASPECT_5_4', label: 'Landscape (5:4)' },
  ];

  // Sync mainVersionId when project prop changes
  useEffect(() => {
    if (project?.mainVersionId) {
      setMainVersionId(project.mainVersionId);
    } else if (project?.currentVersion) {
      setMainVersionId(project.currentVersion);
    }
  }, [project?.mainVersionId, project?.currentVersion]);

  // Sync aspect ratio with main version
  useEffect(() => {
    if (mainVersion?.aspectRatio) {
      setAspectRatio(mainVersion.aspectRatio);
    }
  }, [mainVersionId, mainVersion?.aspectRatio]);

  // Listen to processing_projects for regeneration progress for THIS project
  useEffect(() => {
    if (!currentUser || !project?.projectId) {
      setRegenerateProgress(null);
      return;
    }

    const processingRef = collection(db, 'users', currentUser.uid, 'processing_projects');
    
    const unsubscribe = onSnapshot(processingRef, (snapshot) => {
      // Find any processing project that is a regeneration for this project
      // Backend creates regeneration entries with ID like: regen_${projectId}_${timestamp}
      let foundRegeneration = null;
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const docId = doc.id;
        
        // Check if this is a regeneration for our project
        // Regeneration IDs start with "regen_" followed by the projectId
        if (docId.startsWith(`regen_${project.projectId}_`)) {
          const createdAtMs = data.createdAt?.toMillis() || Date.now();
          const elapsedSeconds = Math.floor((Date.now() - createdAtMs) / 1000);
          const secondsRemaining = Math.max(0, 30 - elapsedSeconds);
          
          foundRegeneration = {
            id: docId,
            prompt: data.prompt,
            aspectRatio: data.aspectRatio,
            createdAt: createdAtMs,
            secondsRemaining,
          };
        }
      });
      
      if (foundRegeneration) {
        setRegenerateProgress(foundRegeneration);
        setRegenerating(true);
      } else {
        setRegenerateProgress(null);
        // Only set regenerating to false if we were previously regenerating
        // This ensures the button shows success briefly
        if (regenerating) {
          setTimeout(() => {
            setRegenerating(false);
          }, 1000);
        }
      }
    }, (error) => {
      console.error('Error fetching regeneration progress:', error);
    });

    return () => unsubscribe();
  }, [currentUser, project?.projectId]);

  // Update countdown for regeneration progress
  useEffect(() => {
    if (!regenerateProgress) return;

    const interval = setInterval(() => {
      setRegenerateProgress(prev => {
        if (!prev) return null;
        
        const elapsedSeconds = Math.floor((Date.now() - prev.createdAt) / 1000);
        const secondsRemaining = Math.max(0, 30 - elapsedSeconds);
        
        if (secondsRemaining === 0) {
          return null;
        }
        
        return { ...prev, secondsRemaining };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [regenerateProgress?.id]);

  const handleRegenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt for regeneration');
      return;
    }

    if (!canEdit) {
      toast.error(`You've reached the maximum of ${maxEdits} edits for this project`);
      return;
    }

    if ((userProfile?.credits || 0) < 3) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setGenerating(true);
    setRegenerating(true);

    try {
      // Backend automatically uses the main version for regeneration
      const response = await designsAPI.regenerate(
        prompt,
        project.projectId,
        aspectRatio
      );

      toast.success('Regeneration started!');
      await refreshProfile();
      setPrompt('');
      
      // Trigger update in parent component
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error(error.message || 'Failed to regenerate design');
      setRegenerating(false);
    } finally {
      setGenerating(false);
    }
  };

  const handleSetMainVersion = async (versionId) => {
    try {
      const projectRef = doc(db, 'users', currentUser.uid, 'projects', project.projectId);
      await updateDoc(projectRef, {
        mainVersionId: versionId,
        updatedAt: new Date().toISOString(),
      });
      setMainVersionId(versionId);
      toast.success('Main version updated');
    } catch (error) {
      console.error('Error setting main version:', error);
      toast.error('Failed to update main version');
    }
  };

  const handleDownload = async (imageUrl, versionId) => {
    try {
      console.log('Download starting for:', imageUrl);
      
      // Fetch the file with response validation
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fluxara-${project.name}-${versionId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
      toast.success('Design downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed, opening in new tab...');
      
      // Fallback: open in new tab if download fails
      window.open(imageUrl, '_blank');
    }
  };

  const handleDeleteProject = async () => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await projectsAPI.delete(project.projectId);
        toast.success('Project deleted');
        if (onDelete) onDelete();
        onClose();
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl truncate max-w-[600px]">{project.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
          {/* Left - Main Image Preview */}
          <div className="space-y-4">
            <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden">
              <img
                src={mainVersion.finalImageUrl}
                alt="Main version"
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Download Button - Below Image */}
            <Button
              onClick={() => handleDownload(mainVersion.finalImageUrl, mainVersion.versionId)}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            
            {/* Prompt History - Bottom of Left Column */}
            <div>
              <h3 className="font-semibold text-sm mb-2">
                Prompt Evolution
              </h3>
              <div className="space-y-3">
                {promptHistory.map((entry, index) => (
                  <div key={entry.versionId} className="relative">
                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-3 rounded-lg border border-cyan-200 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                          {promptHistory.length - index}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-cyan-700">
                              {index === 0 ? 'Current' : index === promptHistory.length - 1 ? 'Original' : `Edit ${promptHistory.length - index - 1}`}
                            </span>
                          </div>
                          <p className="text-xs text-gray-700 leading-relaxed">
                            {entry.prompt}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Connection line to next version */}
                    {index < promptHistory.length - 1 && (
                      <div className="absolute left-3 -bottom-3 w-0.5 h-3 bg-gradient-to-b from-cyan-300 to-transparent"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Regeneration Controls */}
          <div className="space-y-4">
            {/* Regeneration Progress Display - Inside Dialog */}
            {regenerateProgress && (
              <Card className="border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 to-turquoise-50 shadow-xl">
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="animate-spin w-5 h-5 text-cyan-600" />
                    <h3 className="font-bold text-lg text-cyan-900">Regenerating...</h3>
                  </div>
                  <p className="text-sm text-gray-700 mb-3 italic">"{regenerateProgress.prompt}"</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-medium text-cyan-900">
                      <span>✨ Creating your masterpiece...</span>
                      <span>{regenerateProgress.secondsRemaining}s remaining</span>
                    </div>
                    <Progress 
                      value={((30 - regenerateProgress.secondsRemaining) / 30) * 100} 
                      className="h-2" 
                    />
                  </div>
                </div>
              </Card>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                Regenerate with new prompt
              </label>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-2">
                <Textarea
                  placeholder={
                    canEdit
                      ? 'e.g., Make it more colorful, add a sunset background...'
                      : 'Edit limit reached - cannot regenerate'
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={generating || regenerating || !canEdit}
                  rows={1}
                  className="w-full px-3 py-2 text-base leading-6 outline-none bg-transparent border-0 shadow-none focus-visible:ring-0 resize-none min-h-[44px] !max-h-[280px] overflow-y-auto"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleRegenerate();
                    }
                  }}
                  style={{
                    height: 'auto',
                  }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 280) + 'px';
                  }}
                />
              </div>
            </div>

            {/* Aspect Ratio and Regenerate Button */}
            <div className="flex gap-3">
              <Select value={aspectRatio} onValueChange={setAspectRatio} disabled={generating || regenerating || !canEdit}>
                <SelectTrigger className="w-40 h-11 border-0 bg-gray-100 hover:bg-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {aspectRatios.map((ratio) => (
                    <SelectItem key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                className="flex-1 bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 text-gray-900 font-bold h-11 rounded-full transition-all duration-300 shadow-lg"
                onClick={handleRegenerate}
                disabled={generating || regenerating || !canEdit}
              >
                {(generating || regenerating) ? (
                  <>
                    <Loader2 className="animate-spin mr-2 w-4 h-4" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 w-4 h-4" />
                    <span>Regenerate</span>
                    <span className="sm:hidden md:inline ml-1">(19 credits)</span>
                  </>
                )}
              </Button>
            </div>

            {!canEdit && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                You've reached the maximum of {maxEdits} edits for this project. 
                Create a new project to continue designing.
              </div>
            )}

            {/* Version History - Moved to right column */}
            <div className="pt-2">
              <h3 className="font-semibold text-base mb-3">Version History ({versions.length})</h3>
              <div className="grid grid-cols-2 gap-2">
                {[...versions].reverse().map((version, index) => {
                  const isMain = version.versionId === mainVersionId;
                  const isExpanded = expandedVersion === version.versionId;
                  const actualIndex = versions.length - index;
                  
                  return (
                    <Card key={version.versionId} className={`p-2 ${isMain ? 'border-2 border-cyan-500' : ''} ${isExpanded ? 'col-span-2' : ''}`}>
                      <div className="flex flex-col gap-2">
                        <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-100 relative">
                          <img
                            src={version.finalImageUrl}
                            alt={`Version ${actualIndex}`}
                            className="w-full h-full object-cover"
                          />
                          {/* Main Badge at Top Left */}
                          {isMain && (
                            <Badge variant="default" className="absolute top-2 left-2 text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Main
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-xs">V{actualIndex}</span>
                            <div className="flex items-center gap-0.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSetMainVersion(version.versionId)}
                                disabled={isMain}
                                className="h-6 w-6 p-0"
                              >
                                {isMain ? (
                                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                ) : (
                                  <StarOff className="w-3 h-3" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setExpandedVersion(isExpanded ? null : version.versionId)}
                                className="h-6 w-6 p-0"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="mt-2 space-y-2 pt-2 border-t">
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-1">Prompt:</p>
                                <p className="text-xs text-gray-600 bg-gray-50 p-1.5 rounded leading-relaxed">
                                  {version.originalPrompt}
                                </p>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownload(version.finalImageUrl, version.versionId)}
                                  className="h-7"
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Delete Button - Bottom Left */}
        <div className="mt-6 pt-4 border-t">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteProject}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete Project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDialog;
